import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchLogActions, fetchLogStaffOptions, fetchSalonLogs } from '../../api/logs';
import DashboardShell from '../../components/ui/DashboardShell';
import DatePicker from '../../components/ui/DatePicker';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import { activityIcon, describeActivity, groupLogsByDate } from '../../utils/activityLabels';
import { activityActionLabel } from '../../i18n/helpers';

const roleBadgeClass = {
  SALON_MANAGER: 'bg-glow-600/20 text-glow-300',
  SALON_STAFF: 'bg-blue-500/20 text-blue-300',
  CLIENT: 'bg-zinc-600/20 text-zinc-700 dark:text-zinc-300',
};

const SalonActivityLogsPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: staffOpts } = useQuery({
    queryKey: ['log-staff-options'],
    queryFn: fetchLogStaffOptions,
  });

  const { data: actionsData } = useQuery({
    queryKey: ['log-actions'],
    queryFn: fetchLogActions,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['salon-logs', page, pageSize, userId, action, startDate, endDate],
    queryFn: () =>
      fetchSalonLogs({
        page,
        limit: pageSize,
        userId: userId || undefined,
        action: action || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
  });

  const logs = data?.data || [];
  const grouped = groupLogsByDate(logs);
  const pagination = data?.pagination;

  return (
    <DashboardShell title={t('salon.activityLogs.title')} subtitle={t('salon.activityLogs.subtitle')}>
      <div className="sticky top-0 z-10 mb-6 grid gap-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/95 dark:bg-zinc-950/95 p-4 backdrop-blur sm:grid-cols-2 lg:grid-cols-4">
        <DatePicker id="log-start" label={t('common.from')} compact value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
        <DatePicker id="log-end" label={t('common.to')} compact value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
        <select value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }} className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm">
          <option value="">{t('salon.activityLogs.allStaff')}</option>
          {(staffOpts?.staff || []).map((s) => (
            <option key={s._id} value={s._id}>
              {s.firstName} {s.lastName} ({t(`roles.${s.role}`, { defaultValue: s.role })})
            </option>
          ))}
        </select>
        <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm">
          <option value="">{t('activity.allActions')}</option>
          {(actionsData?.actions || []).map((a) => (
            <option key={a} value={a}>{activityActionLabel(a)}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      <div className="space-y-8">
        {Object.entries(grouped).map(([date, entries]) => (
          <div key={date}>
            <h3 className="mb-3 text-sm font-medium text-zinc-600 dark:text-zinc-500">{date}</h3>
            <div className="space-y-3">
              {entries.map((log) => (
                <div key={log.id} className="flex gap-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <span className="text-xl">{activityIcon(log.action)}</span>
                  <div className="flex-1">
                    <p className="text-sm">{describeActivity(log)}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {log.actor && (
                        <span className="text-xs text-zinc-600 dark:text-zinc-400">
                          {log.actor.firstName} {log.actor.lastName}
                        </span>
                      )}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] ${roleBadgeClass[log.actor?.role] || 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-500'}`}>
                        {t(`roles.${log.actor?.role || log.userRole}`, { defaultValue: log.actor?.role?.replace(/_/g, ' ') || log.userRole })}
                      </span>
                      <span className="text-xs text-zinc-600 dark:text-zinc-500">
                        {new Date(log.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />
    </DashboardShell>
  );
};

export default SalonActivityLogsPage;
